import api from "./api";
import { isAndroidApp } from "./platform";

// The Firebase messaging service worker lives at the app root. We register it
// under a dedicated scope so it never fights the PWA worker at "/".
const SW_URL = "/firebase-messaging-sw.js";
const SW_SCOPE = "/firebase-cloud-messaging-push-scope";

let _cfgPromise = null;

export async function getPushConfig() {
  if (!_cfgPromise) {
    _cfgPromise = api
      .get("/push/config")
      .then((r) => r.data)
      .catch(() => ({ configured: false }));
  }
  return _cfgPromise;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

// "unsupported" | "default" | "granted" | "denied"
export function permissionState() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission;
}

async function loadMessaging(cfg) {
  const { initializeApp, getApps } = await import("firebase/app");
  const { getMessaging, getToken, onMessage, isSupported } = await import(
    "firebase/messaging"
  );
  if (!(await isSupported())) return null;
  const appInst = getApps().length ? getApps()[0] : initializeApp(cfg.web_config);
  return { messaging: getMessaging(appInst), getToken, onMessage };
}

async function registerSw(cfg) {
  const url = `${SW_URL}?config=${encodeURIComponent(JSON.stringify(cfg.web_config))}`;
  return navigator.serviceWorker.register(url, { scope: SW_SCOPE });
}

function withTimeout(promise, ms, message) {
  let t;
  return Promise.race([promise, new Promise((_, rej) => { t = setTimeout(() => rej(new Error(message)), ms); })])
    .finally(() => clearTimeout(t));
}

// Waits for *this* registration to activate. navigator.serviceWorker.ready tracks the
// page-scope worker instead and can hang forever (the "Enabling…" freeze).
function waitActive(reg) {
  if (reg.active) return Promise.resolve(reg);
  const sw = reg.installing || reg.waiting;
  return new Promise((resolve) => {
    if (!sw) return resolve(reg);
    sw.addEventListener("statechange", () => sw.state === "activated" && resolve(reg));
  });
}

export const PUSH_BLOCKED = "push_blocked";

const LAST_ERR = "debii_push_last_error";

function blockedError() {
  const e = new Error(PUSH_BLOCKED);
  e.code = PUSH_BLOCKED;
  return e;
}

async function permApi() {
  try { return (await navigator.permissions.query({ name: "notifications" })).state; } catch { return "n/a"; }
}

async function recordError(step, err, extra = {}) {
  const rec = {
    at: new Date().toISOString(), step, code: err?.code || err?.name || "", message: String(err?.message || err),
    sync_permission: "Notification" in window ? Notification.permission : "n/a", permissions_api: await permApi(), ...extra,
  };
  localStorage.setItem(LAST_ERR, JSON.stringify(rec));
  return rec;
}

export function lastPushError() {
  try { return JSON.parse(localStorage.getItem(LAST_ERR) || "null"); } catch { return null; }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function pushPermission(reg) {
  try { return await reg.pushManager.permissionState({ userVisibleOnly: true }); } catch { return "n/a"; }
}

// Samsung Internet can grant via the prompt / push permission while Notification.permission
// stays "default". Wait briefly for it to settle, then fall back to the push permission.
async function settledPermission(reg) {
  for (let i = 0; i < 10; i++) {
    if (Notification.permission === "granted") return "granted";
    await sleep(200);
  }
  return (await pushPermission(reg)) === "granted" ? "granted" : Notification.permission;
}

// Firebase re-reads Notification.permission; present the settled value only while it runs.
async function withSettledPermission(fn) {
  if (Notification.permission === "granted") return fn();
  const own = Object.getOwnPropertyDescriptor(Notification, "permission");
  try {
    Object.defineProperty(Notification, "permission", { get: () => "granted", configurable: true });
    return await fn();
  } finally {
    if (own) Object.defineProperty(Notification, "permission", own);
    else delete Notification.permission;
  }
}

export function deviceMeta() {
  const ua = navigator.userAgent || "";
  const browser = /SamsungBrowser\/(\d+)/.exec(ua) ? `Samsung Internet ${RegExp.$1}`
    : /EdgA?\/(\d+)/.exec(ua) ? `Edge ${RegExp.$1}`
    : /Firefox\/(\d+)/.exec(ua) ? `Firefox ${RegExp.$1}`
    : /Chrome\/(\d+)/.exec(ua) ? `Chrome ${RegExp.$1}`
    : /Version\/(\d+).*Safari/.exec(ua) ? `Safari ${RegExp.$1}` : "Browser";
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;
  return { surface: isAndroidApp() ? "android_app" : standalone ? "installed_web_app" : "browser", browser };
}

async function getTokenFor(m, cfg, registration) {
  return withSettledPermission(() => m.getToken(m.messaging, { vapidKey: cfg.vapid_key, serviceWorkerRegistration: registration }));
}

const DEVICE_KEY = "debii_push_device";
export const currentDeviceId = () => localStorage.getItem(DEVICE_KEY);

async function registerToken(token, source) {
  const { data } = await api.post("/push/register", { token, source, ...deviceMeta() });
  if (data?.device_id) localStorage.setItem(DEVICE_KEY, data.device_id);
}

async function doEnable() {
  const cfg = await getPushConfig();
  if (!cfg.configured) throw new Error("Push notifications aren't available yet.");
  if (!pushSupported())
    throw new Error("This browser doesn't support push notifications.");
  const registration = await waitActive(await registerSw(cfg));
  let perm = Notification.permission;
  // Prompt only when nothing is granted yet — never re-prompt a granted push permission.
  if (perm !== "granted" && (await pushPermission(registration)) !== "granted") {
    try {
      perm = await Notification.requestPermission();
    } catch (e) {
      await recordError("request_permission", e);
      throw blockedError();
    }
    if (perm !== "granted") {
      await recordError("request_permission", new Error(`result=${perm}`), { request_result: perm });
      throw blockedError();
    }
  }
  if ((await settledPermission(registration)) !== "granted") {
    await recordError("stale_permission", new Error("granted by prompt, page still reports " + Notification.permission), { request_result: perm });
    throw blockedError();
  }

  const m = await loadMessaging(cfg);
  if (!m) throw new Error("Push isn't supported in this browser.");

  let token;
  try {
    token = await getTokenFor(m, cfg, registration);
  } catch (e) {
    await recordError("get_token", e, { request_result: perm });
    if (String(e?.code || e?.message).includes("permission-blocked")) throw blockedError();
    throw new Error("Couldn't connect to the notification service. Please try again.");
  }
  if (!token) throw new Error("Couldn't get a notification token. Please try again.");

  try {
    await registerToken(token);
  } catch (e) {
    await recordError("backend_register", e);
    throw e;
  }
  localStorage.removeItem(LAST_ERR);
  localStorage.setItem(PUSH_FLAG, "on");
  return token;
}

export function enablePush() {
  return withTimeout(doEnable(), 30000, "This is taking too long. Check your connection and try again.");
}

const PUSH_FLAG = "debii_push";
const SYNC_AT = "debii_push_synced_at";

// Re-registers this device's current FCM token on app open/resume (tokens can rotate or be
// pruned server-side). Never prompts; respects an explicit "turn off".
export async function syncPushToken({ force = false } = {}) {
  try {
    if (!pushSupported() || Notification.permission === "denied") return;
    if (localStorage.getItem(PUSH_FLAG) === "off") return;
    const last = Number(localStorage.getItem(SYNC_AT) || 0);
    if (!force && Date.now() - last < 6 * 3600 * 1000) return;
    const cfg = await getPushConfig();
    if (!cfg.configured) return;
    const { data: st } = await api.get("/push/status");
    if (st.opted_out) return;
    if (localStorage.getItem(PUSH_FLAG) !== "on" && !st.enabled) return;
    const existing = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    existing?.update().catch(() => {});
    if (!existing) return;
    const registration = await waitActive(existing);
    if (Notification.permission !== "granted" && (await pushPermission(registration)) !== "granted") return;
    const m = await loadMessaging(cfg);
    if (!m) return;
    const token = await getTokenFor(m, cfg, registration);
    if (!token) return;
    await registerToken(token, "sync");
    localStorage.setItem(PUSH_FLAG, "on");
    localStorage.setItem(SYNC_AT, String(Date.now()));
  } catch {
    /* best effort */
  }
}

export async function disablePush() {
  // Best-effort: tell the backend to drop this device's token, then clear all.
  try {
    const cfg = await getPushConfig();
    if (cfg.configured && pushSupported() && Notification.permission !== "denied") {
      const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
      const m = await loadMessaging(cfg);
      if (m && reg) {
        try {
          const token = (await pushPermission(reg)) === "granted" ? await getTokenFor(m, cfg, reg) : null;
          if (token) await api.post("/push/unregister", { token });
        } catch {
          /* ignore */
        }
      }
    }
  } catch {
    /* ignore */
  }
  await api.post("/push/unregister", {});
  localStorage.setItem(PUSH_FLAG, "off");
}

// Foreground messages: fired only while a tab is focused. Returns an unsubscribe fn.
export async function listenForeground(callback) {
  try {
    const cfg = await getPushConfig();
    if (!cfg.configured || !pushSupported()) return () => {};
    if (Notification.permission !== "granted") return () => {};
    const m = await loadMessaging(cfg);
    if (!m) return () => {};
    return m.onMessage(m.messaging, callback);
  } catch {
    return () => {};
  }
}
